import { useState } from 'react';
import type { InvitationResult } from '@kontor/shared';

const dateFormat = new Intl.DateTimeFormat('de-DE', { dateStyle: 'long', timeStyle: 'short' });

/** Adresse der Seite, auf der der eingeladene Benutzer sein Passwort festlegt */
export function invitationUrl(token: string): string {
  return `${window.location.origin}/einladung/${token}`;
}

/**
 * Zeigt den Einladungslink einmalig an. Der Token wird nirgends gespeichert und lässt sich später nicht mehr abrufen,
 * wer ihn verliert, erzeugt einen neuen Link. Ist der Mailversand eingerichtet, geht er zusätzlich per E-Mail raus, sonst gibt man ihn persönlich weiter.
 */
export function InvitationLink({ result, mailRequested = false, onClose }: { result: InvitationResult; mailRequested?: boolean; onClose?: () => void }) {
  const [copied, setCopied] = useState(false);
  const url = invitationUrl(result.token);

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      // Ohne Zugriff auf die Zwischenablage bleibt das Markieren des Felds
      setCopied(false);
    }
  }

  return (
    <section className="card invitation" aria-live="polite">
      <h2>Einladungslink für {result.user.name}</h2>
      {result.mailSent ? (
        <p>
          Eine E-Mail mit dem Link wurde an {result.user.email} geschickt. Über den Link legt {result.user.name} das Passwort selbst fest und meldet sich danach mit dieser Adresse an.
          Sie können den Link zusätzlich selbst weitergeben.
        </p>
      ) : (
        <>
          {mailRequested && (
            <div className="alert alert--error" role="alert">
              Die E-Mail konnte nicht verschickt werden. Geben Sie den Link stattdessen persönlich weiter.
            </div>
          )}
          <p>
            Geben Sie diesen Link persönlich an {result.user.name} weiter. Über den Link legt die Person ihr Passwort selbst fest und meldet sich danach mit {result.user.email} an.
          </p>
        </>
      )}
      <div className="invitation__row">
        <input className="input" readOnly value={url} aria-label="Einladungslink" onFocus={(e) => e.currentTarget.select()} />
        <button type="button" className="btn btn--primary" onClick={copy}>{copied ? 'Kopiert' : 'Link kopieren'}</button>
      </div>
      <p className="muted small">
        Der Link gilt bis {dateFormat.format(new Date(result.expiresAt))} und lässt sich nur einmal benutzen. Er wird nur jetzt angezeigt. Geht er verloren, erzeugen Sie in der
        Benutzerliste einen neuen.
      </p>
      {onClose && <button type="button" className="btn btn--secondary btn--sm" onClick={onClose}>Schließen</button>}
    </section>
  );
}
